// ─── Gestionnaire de tâches du poste (canal « processus ») ───
// Protocole Telipso Assistance v2 §2 « Processus et services » + extensions RMM
// (cpu, session, arbre). Requêtes corrélées par `id`. Le canal n'est ouvert qu'à
// la première ouverture du panneau.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, RefreshCw, Search, XCircle } from 'lucide-react';
import { useChannel } from './useChannel.js';
import { ClientFichiers, fmtTaille } from './fichiersClient.js';
import { useTableSort } from '../lib/useTableSort.jsx';

const btn = 'inline-flex items-center gap-1 bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2 cursor-pointer hover:text-fg disabled:opacity-50 disabled:cursor-default';
const sel = 'bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2';
const input = 'bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg';
const th = 'text-left font-semibold px-2 py-1.5 border-b border-rule whitespace-nowrap';
const td = 'px-2 py-1 border-b border-rule whitespace-nowrap overflow-hidden text-ellipsis';

const ETATS = {
  running: ['En cours', 'bg-ok/15 text-ok'],
  stopped: ['Arrêté', 'bg-rule text-muted'],
  start_pending: ['Démarrage…', 'bg-warn/15 text-warn'],
  stop_pending: ['Arrêt…', 'bg-warn/15 text-warn'],
  paused: ['En pause', 'bg-warn/15 text-warn'],
  continue_pending: ['Reprise…', 'bg-warn/15 text-warn'],
  pause_pending: ['Pause…', 'bg-warn/15 text-warn'],
};
const DEMARRAGES = { auto: 'Automatique', differe: 'Automatique (différé)', manuel: 'Manuel', desactive: 'Désactivé' };
const ENCOURS = new Set(['running', 'start_pending', 'continue_pending']);

const fmtMem = (o) => (o == null ? '' : fmtTaille(o));
const fmtCpu = (c) => (c == null ? '' : c.toFixed(c < 10 ? 1 : 0) + ' %');

export function TaskManager({ sessionId, visible }) {
  const [demarre, setDemarre] = useState(false);
  const [onglet, setOnglet] = useState('processus');
  const [processus, setProcessus] = useState([]);
  const [services, setServices] = useState([]);
  const [perf, setPerf] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [filtreEtat, setFiltreEtat] = useState('tous');
  const [pause, setPause] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState('');
  const procTri = useTableSort('cpu', 'desc');
  const svcTri = useTableSort('libelle');

  const canalRef = useRef(null);
  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = new ClientFichiers(() => canalRef.current);
  const client = clientRef.current;
  const occupe = useRef({ lister: false, services: false, performances: false });

  useEffect(() => {
    if (visible) setDemarre(true);
  }, [visible]);

  const canal = useChannel(
    sessionId,
    'processus',
    (e) => client.traiter(e),
    undefined,
    { enabled: demarre, onClose: () => client.rejeterTout('Canal fermé') },
  );
  canalRef.current = canal;
  const ouvert = canal.etat === 'ouvert';

  // Lecture sans chevauchement : une réponse lente n'empile pas d'autres requêtes.
  const lire = useCallback(async (type, appliquer) => {
    if (occupe.current[type]) return;
    occupe.current[type] = true;
    try {
      appliquer(await client.demander({ t: type }));
      if (type !== 'performances') setErreur('');
    } catch (err) {
      if (type !== 'performances') setErreur(err.message);
    } finally {
      occupe.current[type] = false;
    }
  }, [client]);

  const lireProcessus = useCallback(() => lire('lister', (m) => setProcessus(m.liste || [])), [lire]);
  const lireServices = useCallback(() => lire('services', (m) => setServices(m.liste || [])), [lire]);

  const actif = visible && ouvert;

  // Performances toutes les 2 s.
  useEffect(() => {
    if (!actif) return undefined;
    const f = () => lire('performances', (m) => setPerf(m));
    f();
    const iv = setInterval(f, 2000);
    return () => clearInterval(iv);
  }, [actif, lire]);

  // Processus toutes les 3 s (sauf pause) ; services à l'ouverture de l'onglet et sur demande.
  useEffect(() => {
    if (!actif || onglet !== 'processus') return undefined;
    lireProcessus();
    if (pause) return undefined;
    const iv = setInterval(lireProcessus, 3000);
    return () => clearInterval(iv);
  }, [actif, onglet, pause, lireProcessus]);
  useEffect(() => {
    if (actif && onglet === 'services') lireServices();
  }, [actif, onglet, lireServices]);

  async function terminer(p, arbre) {
    const msg = arbre
      ? `Terminer « ${p.nom} » (PID ${p.pid}) et tous ses processus enfants ?`
      : `Terminer le processus « ${p.nom} » (PID ${p.pid}) ? Les données non enregistrées seront perdues.`;
    if (!window.confirm(msg)) return;
    setEnCours('p' + p.pid);
    try {
      await client.demander({ t: 'terminer', pid: p.pid, arbre });
      setErreur('');
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnCours('');
      lireProcessus();
    }
  }

  async function actionService(s, message, libelle) {
    setEnCours('s' + s.nom);
    try {
      await client.demander({ t: 'service', nom: s.nom, ...message });
      setErreur('');
    } catch (err) {
      setErreur(`${libelle} « ${s.libelle || s.nom} » : ${err.message}`);
    } finally {
      setEnCours('');
      lireServices();
    }
  }

  const changerDemarrage = (s, valeur) => {
    if (valeur === s.demarrage) return;
    if (valeur === 'desactive' && !window.confirm(`Désactiver le service « ${s.libelle || s.nom} » ? Il ne pourra plus démarrer tant qu’il ne sera pas réactivé.`)) return;
    actionService(s, { action: 'demarrage', demarrage: valeur }, 'Changement du démarrage de');
  };

  const f = filtre.trim().toLowerCase();
  const procs = processus.filter((p) => !f || `${p.nom} ${p.pid} ${p.utilisateur || ''} ${p.commande || ''}`.toLowerCase().includes(f));
  const procsTries = procTri.sortRows(procs, (p, k) => (k === 'memoire' || k === 'cpu' || k === 'pid' || k === 'session' ? p[k] ?? -1 : p[k] || ''));
  const svcs = services.filter((s) => {
    if (filtreEtat === 'running' && !ENCOURS.has(s.etat)) return false;
    if (filtreEtat === 'stopped' && s.etat !== 'stopped') return false;
    return !f || `${s.libelle || ''} ${s.nom} ${s.description || ''}`.toLowerCase().includes(f);
  });
  const svcsTries = svcTri.sortRows(svcs, (s, k) => (k === 'pid' ? s.pid ?? -1 : s[k] || ''));

  const pctMem = perf?.memoireTotale ? (perf.memoireUtilisee / perf.memoireTotale) * 100 : null;

  return (
    <div className={`${visible ? 'flex' : 'hidden'} flex-col flex-1 min-h-0`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5 border-b border-rule text-[11px] text-muted">
        <span>
          CPU <b className="text-fg">{perf?.cpu != null ? Math.round(perf.cpu) + ' %' : '—'}</b>
        </span>
        <span>
          Mémoire{' '}
          <b className="text-fg">
            {perf?.memoireUtilisee != null ? `${fmtMem(perf.memoireUtilisee)} / ${fmtMem(perf.memoireTotale)}` : '—'}
          </b>
          {pctMem != null && ` (${Math.round(pctMem)} %)`}
        </span>
        {!ouvert && <span className="text-warn">{canal.etat === 'ferme' ? canal.raison || 'Canal fermé' : 'Connexion…'}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-rule">
        <div className="inline-flex rounded-lg border border-rule-3 overflow-hidden">
          {[['processus', 'Processus'], ['services', 'Services']].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setOnglet(k)}
              className={`px-3 py-1 text-xs cursor-pointer border-none ${onglet === k ? 'bg-blue-500 text-white font-semibold' : 'bg-card text-muted hover:text-fg'}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-2" />
          <input
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder="Filtrer…"
            className={`${input} pl-6 w-44`}
            aria-label="Filtrer"
          />
        </div>
        {onglet === 'services' && (
          <select className={sel} value={filtreEtat} onChange={(e) => setFiltreEtat(e.target.value)} aria-label="Filtrer par état">
            <option value="tous">Tous les états</option>
            <option value="running">En cours</option>
            <option value="stopped">Arrêtés</option>
          </select>
        )}
        <div className="flex-1" />
        {onglet === 'processus' && (
          <button className={btn} onClick={() => setPause((p) => !p)} title="Rafraîchissement automatique toutes les 3 s">
            {pause ? <Play size={12} /> : <Pause size={12} />} {pause ? 'Reprendre' : 'Pause'}
          </button>
        )}
        <button className={btn} onClick={onglet === 'processus' ? lireProcessus : lireServices} disabled={!ouvert}>
          <RefreshCw size={12} /> Actualiser
        </button>
        <span className="text-[11px] text-muted-2">
          {onglet === 'processus' ? procs.length + ' processus' : svcs.length + ' services'}
        </span>
      </div>

      {erreur && (
        <div role="alert" className="flex items-start gap-2 px-3 py-1.5 border-b border-rule bg-crit/10 text-crit text-xs">
          <span className="flex-1 break-words">{erreur}</span>
          <button className="bg-transparent border-none text-crit cursor-pointer" onClick={() => setErreur('')} aria-label="Masquer l’erreur">
            <XCircle size={14} />
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {onglet === 'processus' ? (
          <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 bg-card text-muted">
              <tr>
                <th className={th} {...procTri.headerProps('nom', { width: '22%' })}>Nom{procTri.arrow('nom')}</th>
                <th className={th} {...procTri.headerProps('pid', { width: '9%' })}>PID{procTri.arrow('pid')}</th>
                <th className={th} {...procTri.headerProps('utilisateur', { width: '22%' })}>Utilisateur{procTri.arrow('utilisateur')}</th>
                <th className={th} {...procTri.headerProps('cpu', { width: '9%' })}>CPU{procTri.arrow('cpu')}</th>
                <th className={th} {...procTri.headerProps('memoire', { width: '12%' })}>Mémoire{procTri.arrow('memoire')}</th>
                <th className={th} {...procTri.headerProps('session', { width: '8%' })}>Sess.{procTri.arrow('session')}</th>
                <th className={th} style={{ width: '18%' }}></th>
              </tr>
            </thead>
            <tbody>
              {procsTries.map((p) => (
                <tr key={p.pid} className="hover:bg-rule/40" title={p.commande || undefined}>
                  <td className={td}>{p.nom}</td>
                  <td className={`${td} tabular-nums`}>{p.pid}</td>
                  <td className={td}>{p.utilisateur}</td>
                  <td className={`${td} tabular-nums`}>{fmtCpu(p.cpu)}</td>
                  <td className={`${td} tabular-nums`}>{fmtMem(p.memoire)}</td>
                  <td className={`${td} tabular-nums`}>{p.session ?? ''}</td>
                  <td className={`${td} text-right`}>
                    <button className={btn} disabled={enCours === 'p' + p.pid} onClick={() => terminer(p, false)}>Terminer</button>{' '}
                    <button className={btn} disabled={enCours === 'p' + p.pid} onClick={() => terminer(p, true)} title="Terminer le processus et tous ses descendants">Arbre</button>
                  </td>
                </tr>
              ))}
              {procsTries.length === 0 && (
                <tr><td className="px-3 py-4 text-muted-2" colSpan={7}>{ouvert ? 'Aucun processus.' : 'En attente du poste…'}</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 bg-card text-muted">
              <tr>
                <th className={th} {...svcTri.headerProps('libelle', { width: '24%' })}>Libellé{svcTri.arrow('libelle')}</th>
                <th className={th} {...svcTri.headerProps('nom', { width: '16%' })}>Nom{svcTri.arrow('nom')}</th>
                <th className={th} {...svcTri.headerProps('etat', { width: '12%' })}>État{svcTri.arrow('etat')}</th>
                <th className={th} {...svcTri.headerProps('demarrage', { width: '16%' })}>Démarrage{svcTri.arrow('demarrage')}</th>
                <th className={th} {...svcTri.headerProps('pid', { width: '7%' })}>PID{svcTri.arrow('pid')}</th>
                <th className={th} style={{ width: '25%' }}></th>
              </tr>
            </thead>
            <tbody>
              {svcsTries.map((s) => {
                const [lib, cls] = ETATS[s.etat] || [s.etat, 'bg-rule text-muted'];
                const marche = ENCOURS.has(s.etat) || s.etat === 'paused';
                const stoppe = s.etat === 'stopped';
                const occ = enCours === 's' + s.nom;
                return (
                  <tr key={s.nom} className="hover:bg-rule/40" title={s.description || undefined}>
                    <td className={td}>{s.libelle || s.nom}</td>
                    <td className={`${td} text-muted`}>{s.nom}</td>
                    <td className={td}>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{lib}</span>
                    </td>
                    <td className={td}>
                      <select
                        className={`${sel} max-w-full`}
                        value={s.demarrage}
                        disabled={occ}
                        onChange={(e) => changerDemarrage(s, e.target.value)}
                        aria-label={'Type de démarrage de ' + (s.libelle || s.nom)}
                      >
                        {!DEMARRAGES[s.demarrage] && <option value={s.demarrage}>{s.demarrage || '?'}</option>}
                        {Object.entries(DEMARRAGES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </td>
                    <td className={`${td} tabular-nums`}>{s.pid || ''}</td>
                    <td className={`${td} text-right`}>
                      {occ ? (
                        <span className="text-muted-2">Action en cours…</span>
                      ) : (
                        <>
                          {stoppe && <button className={btn} onClick={() => actionService(s, { action: 'demarrer' }, 'Démarrage de')}>Démarrer</button>}
                          {marche && (
                            <>
                              <button className={btn} onClick={() => actionService(s, { action: 'arreter' }, 'Arrêt de')}>Arrêter</button>{' '}
                              <button className={btn} onClick={() => actionService(s, { action: 'redemarrer' }, 'Redémarrage de')}>Redémarrer</button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {svcsTries.length === 0 && (
                <tr><td className="px-3 py-4 text-muted-2" colSpan={6}>{ouvert ? 'Aucun service.' : 'En attente du poste…'}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
